import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { initiatePayment, formatPrice, getAnnualPrice } from '../services/payment';
import { 
  Check, 
  X, 
  Zap, 
  Star, 
  Building2, 
  Users, 
  Clock, 
  FileText,
  BarChart3,
  Headphones,
  Crown,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import './Pricing.css';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    credits: 5,
    price: 0,
    pricePerCredit: null,
    discount: null,
    popular: false,
    features: {
      doctors: 1,
      history: '7 days',
      exportPdf: false,
      exportExcel: false,
      analytics: false,
      adminDashboard: false,
      customBranding: false,
      support: 'Email'
    }
  },
  {
    id: 'starter',
    name: 'Starter',
    credits: 30,
    price: 599,
    pricePerCredit: 19.97,
    discount: null,
    popular: false,
    features: {
      doctors: 1,
      history: '30 days',
      exportPdf: true,
      exportExcel: false,
      analytics: false,
      adminDashboard: false,
      customBranding: false,
      support: 'Email'
    }
  },
  {
    id: 'basic',
    name: 'Basic',
    credits: 100,
    price: 1499,
    pricePerCredit: 14.99,
    discount: '25% off',
    popular: true,
    features: {
      doctors: 3,
      history: '90 days',
      exportPdf: true,
      exportExcel: true,
      analytics: 'Basic',
      adminDashboard: false,
      customBranding: false,
      support: 'Priority'
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 300,
    price: 3499,
    pricePerCredit: 11.66,
    discount: '42% off',
    popular: false,
    features: {
      doctors: 10,
      history: '1 year',
      exportPdf: true,
      exportExcel: true,
      analytics: 'Full',
      adminDashboard: true,
      customBranding: false,
      support: 'Phone + Email'
    }
  },
  {
    id: 'hospital',
    name: 'Hospital',
    credits: 1000,
    price: 9999,
    pricePerCredit: 9.99,
    discount: '50% off',
    popular: false,
    features: {
      doctors: 'Unlimited',
      history: 'Unlimited',
      exportPdf: true,
      exportExcel: true,
      analytics: 'Full',
      adminDashboard: true,
      customBranding: true,
      support: 'Dedicated Manager'
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    credits: 5000,
    price: 39999,
    pricePerCredit: 8.00,
    discount: '60% off',
    popular: false,
    features: {
      doctors: 'Unlimited',
      history: 'Unlimited',
      exportPdf: true,
      exportExcel: true,
      analytics: 'Full + Custom',
      adminDashboard: true,
      customBranding: true,
      support: 'Dedicated Team'
    }
  }
];

function Pricing({ onClose, onPaymentSuccess }) {
  const { currentUser, userProfile, isSuperAdmin, refreshProfile } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null); // planId being processed
  const [paymentStatus, setPaymentStatus] = useState(null); // { type: 'success' | 'error', message: string }
  
  // Check user's plan first, then organization's plan, default to free
  const currentPlan = userProfile?.plan || userProfile?.organization?.plan || 'free';

  const handleSelectPlan = async (plan) => {
    if (plan.id === 'free' || plan.id === currentPlan || loading) return;
    
    setLoading(plan.id);
    setPaymentStatus(null);
    
    initiatePayment({
      planId: plan.id,
      billingCycle: billingCycle,
      userId: currentUser.uid,
      userEmail: currentUser.email,
      userName: userProfile?.name || 'User',
      organizationId: userProfile?.organizationId,
      organizationName: userProfile?.organizationName,
      phone: userProfile?.phone || '',
      onSuccess: async (result) => {
        setLoading(null);
        setPaymentStatus({
          type: 'success',
          message: 'Payment successful! ' + result.credits + ' credits added to your account.'
        });
        
        // Refresh user profile to get updated credits
        if (refreshProfile) {
          await refreshProfile();
        }
        
        // Callback to parent
        if (onPaymentSuccess) {
          onPaymentSuccess(result);
        }
        
        // Auto close after 3 seconds
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      },
      onError: (errorMessage) => {
        setLoading(null);
        setPaymentStatus({
          type: 'error',
          message: errorMessage
        });
      },
      onDismiss: () => {
        setLoading(null);
      }
    });
  };

  if (isSuperAdmin) {
    return (
      <div className="pricing-container">
        <div className="pricing-header">
          {onClose && (
            <button className="back-btn" onClick={onClose}>
              <ArrowLeft size={20} />
              Back
            </button>
          )}
          <h1>Pricing Plans</h1>
          <p>You have Super Admin access with unlimited credits</p>
        </div>
        <div className="superadmin-notice">
          <Crown size={48} />
          <h2>Super Admin</h2>
          <p>You have unlimited access to all features and AI models at no cost.</p>
          {onClose && (
            <button className="btn-primary" onClick={onClose}>
              Continue to App
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        {onClose && (
          <button className="back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
            Back
          </button>
        )}
        <h1>Choose Your Plan</h1>
        <p>Select the perfect plan for your practice</p>
        
        <div className="billing-toggle">
          <button 
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button 
            className={billingCycle === 'annual' ? 'active' : ''}
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <span className="save-badge">Save 15%</span>
          </button>
        </div>
      </div>

      {/* Payment Status Messages */}
      {paymentStatus && (
        <div className={'payment-status ' + paymentStatus.type}>
          {paymentStatus.type === 'success' ? (
            <CheckCircle size={24} />
          ) : (
            <AlertCircle size={24} />
          )}
          <span>{paymentStatus.message}</span>
          <button onClick={() => setPaymentStatus(null)}>×</button>
        </div>
      )}

      <div className="pricing-grid">
        {PLANS.map(plan => {
          const isCurrentPlan = plan.id === currentPlan;
          const price = billingCycle === 'annual' ? getAnnualPrice(plan.price) : plan.price;
          const isLoading = loading === plan.id;
          
          return (
            <div 
              key={plan.id} 
              className={'pricing-card ' + (plan.popular ? 'popular ' : '') + (isCurrentPlan ? 'current' : '')}
            >
              {plan.popular && (
                <div className="popular-badge">
                  <Star size={14} />
                  Most Popular
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="current-badge">Current Plan</div>
              )}

              <div className="plan-header">
                <h3>{plan.name}</h3>
                {plan.discount && (
                  <span className="discount-badge">{plan.discount}</span>
                )}
              </div>

              <div className="plan-price">
                <span className="currency">₹</span>
                <span className="amount">{formatPrice(price)}</span>
                <span className="period">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
              </div>

              <div className="plan-credits">
                <Zap size={16} />
                <span><strong>{plan.credits}</strong> AI Credits</span>
                {plan.pricePerCredit && (
                  <span className="per-credit">₹{plan.pricePerCredit}/credit</span>
                )}
              </div>

              <ul className="plan-features">
                <li>
                  <Users size={16} />
                  <span>
                    {typeof plan.features.doctors === 'number' 
                      ? plan.features.doctors + ' Doctor' + (plan.features.doctors > 1 ? 's' : '')
                      : plan.features.doctors + ' Doctors'
                    }
                  </span>
                </li>
                <li>
                  <Clock size={16} />
                  <span>{plan.features.history} History</span>
                </li>
                <li className={plan.features.exportPdf ? '' : 'disabled'}>
                  {plan.features.exportPdf ? <Check size={16} /> : <X size={16} />}
                  <span>Export PDF</span>
                </li>
                <li className={plan.features.exportExcel ? '' : 'disabled'}>
                  {plan.features.exportExcel ? <Check size={16} /> : <X size={16} />}
                  <span>Export Excel</span>
                </li>
                <li className={plan.features.analytics ? '' : 'disabled'}>
                  {plan.features.analytics ? <BarChart3 size={16} /> : <X size={16} />}
                  <span>{plan.features.analytics || 'No'} Analytics</span>
                </li>
                <li className={plan.features.adminDashboard ? '' : 'disabled'}>
                  {plan.features.adminDashboard ? <Check size={16} /> : <X size={16} />}
                  <span>Admin Dashboard</span>
                </li>
                <li className={plan.features.customBranding ? '' : 'disabled'}>
                  {plan.features.customBranding ? <Building2 size={16} /> : <X size={16} />}
                  <span>Custom Branding</span>
                </li>
                <li>
                  <Headphones size={16} />
                  <span>{plan.features.support} Support</span>
                </li>
              </ul>

              <button
                className={'plan-btn ' + (isCurrentPlan ? 'current ' : '') + (plan.popular ? 'popular' : '')}
                onClick={() => handleSelectPlan(plan)}
                disabled={isCurrentPlan || plan.id === 'free' || loading !== null}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="spin" />
                    Processing...
                  </>
                ) : isCurrentPlan ? (
                  'Current Plan'
                ) : plan.id === 'free' ? (
                  'Free Trial'
                ) : (
                  'Upgrade Now'
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="pricing-footer">
        <div className="pricing-note">
          <FileText size={16} />
          <span>All plans include access to all 3 AI models (GPT-4o, Gemini, Claude)</span>
        </div>
        <div className="pricing-note">
          <Zap size={16} />
          <span>1 Credit = 1 AI Model Analysis. Using all 3 models = 3 credits.</span>
        </div>
        <div className="pricing-note secure">
          <Check size={16} />
          <span>Secure payments powered by Razorpay. 100% safe & encrypted.</span>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
